import cronstrue from 'cronstrue';
import { CronExpressionParser } from 'cron-parser';

// AWS EventBridge schedule expressions come in two flavours:
//   - cron(minute hour day-of-month month day-of-week year)  (6 fields)
//   - rate(N minutes|hours|days)
// AWS cron also uses "?" as a placeholder when the matching day-* field is
// specified — cron-parser doesn't understand "?", so we normalise it to "*".
// cron-parser also expects 5-field input, so we drop the trailing year.

export type FormattedSchedule = {
  raw: string;
  human: string;
  nextRunUtc: Date | null;
  isRate: boolean;
};

export function formatAwsSchedule(expr: string | null | undefined): FormattedSchedule | null {
  if (!expr) return null;
  const trimmed = expr.trim();

  const rateMatch = trimmed.match(/^rate\(\s*(\d+)\s+(minutes?|hours?|days?)\s*\)$/i);
  if (rateMatch) {
    const n = rateMatch[1];
    const unit = rateMatch[2].toLowerCase();
    return {
      raw: trimmed,
      human: `Every ${n} ${unit}`,
      nextRunUtc: null,
      isRate: true,
    };
  }

  const cronMatch = trimmed.match(/^cron\((.+)\)$/i);
  const cronBody = cronMatch ? cronMatch[1].trim() : trimmed;

  let human: string;
  try {
    human = `${cronstrue.toString(cronBody, { throwExceptionOnParseError: false })} (UTC)`;
  } catch {
    human = trimmed;
  }

  let parserInput = cronBody.replace(/\?/g, '*');
  const fields = parserInput.split(/\s+/);
  if (fields.length === 6) parserInput = fields.slice(0, 5).join(' ');

  let nextRunUtc: Date | null = null;
  try {
    const it = CronExpressionParser.parse(parserInput, { tz: 'UTC' });
    nextRunUtc = it.next().toDate();
  } catch {
    nextRunUtc = null;
  }

  return {
    raw: trimmed,
    human,
    nextRunUtc,
    isRate: false,
  };
}

export function formatTimeUntil(target: Date): string {
  const ms = target.getTime() - Date.now();
  if (ms < 0) return 'in the past';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'in <1m';
  if (mins < 60) return `in ${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins - hrs * 60;
  if (hrs < 24) return remMins > 0 ? `in ${hrs}h ${remMins}m` : `in ${hrs}h`;
  const days = Math.floor(hrs / 24);
  const remHrs = hrs - days * 24;
  return remHrs > 0 ? `in ${days}d ${remHrs}h` : `in ${days}d`;
}

export function formatUtcDateTime(d: Date): string {
  // YYYY-MM-DD HH:MM UTC
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}
